import {
  Box,
  Button,
  FormControl,
  TextField,
  Typography
} from '@linagora/twake-mui'
import React, { useEffect, useState } from 'react'
import { useI18n } from 'twake-i18n'
import { SettingsTab } from './SettingsTab'
import { CalendarSelector } from './CalendarSelector'
import { useResponsiveInputSize } from '@common/hooks/useResponsiveInputSize'

export const ImportTab: React.FC<{
  userId: string
  importTarget: string
  setImportTarget: (target: string) => void
  setImportedContent: (content: File | null) => void
  setImportedUrl: (url: string) => void
  newCalParams: {
    name: string
    setName: (name: string) => void
    description: string
    setDescription: (d: string) => void
    color: Record<string, string>
    setColor: (color: Record<string, string>) => void
    visibility: 'public' | 'private'
    setVisibility: (visibility: 'public' | 'private') => void
  }
}> = ({
  userId,
  importTarget,
  setImportTarget,
  setImportedContent,
  setImportedUrl,
  newCalParams
}) => {
  const { t } = useI18n()
  const inputSize = useResponsiveInputSize()

  const [importMode, setImportMode] = useState<'file' | 'url'>('file')
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importUrl, setImportUrl] = useState('')

  useEffect(() => {
    setImportedContent(importMode === 'file' ? importFile : null)
    setImportedUrl(importMode === 'url' ? importUrl.trim() : '')
  }, [importFile, importUrl, importMode, setImportedContent, setImportedUrl])

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <Button
          variant={importMode === 'file' ? 'contained' : 'outlined'}
          size="small"
          onClick={() => setImportMode('file')}
        >
          {t('calendar.import_file_mode')}
        </Button>
        <Button
          variant={importMode === 'url' ? 'contained' : 'outlined'}
          size="small"
          onClick={() => setImportMode('url')}
        >
          {t('calendar.import_url_mode')}
        </Button>
      </Box>
      {/* Form group 1: Select file button - first group, margin top 0 */}
      {importMode === 'file' && (
        <Box sx={{ mt: 0 }}>
          <Button
            variant="outlined"
            component="label"
            size="medium"
            sx={{ borderRadius: '12px' }}
          >
            {t('common.select_file')}
            <input
              type="file"
              hidden
              accept=".ics"
              onChange={e => setImportFile(e.target.files?.[0] ?? null)}
            />
          </Button>
          {importFile && (
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', marginTop: '6px' }}
            >
              {importFile.name}
            </Typography>
          )}
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block', marginTop: '6px' }}
          >
            {t('calendar.import_file_description')}
          </Typography>
        </Box>
      )}

      {/* Form group 2: URL field */}
      {importMode === 'url' && (
        <Box sx={{ mt: 0 }}>
          <TextField
            fullWidth
            label={t('calendar.ics_feed_url')}
            value={importUrl}
            onChange={e => setImportUrl(e.target.value)}
            size={inputSize}
            margin="dense"
            sx={{
              '&.MuiFormControl-root.MuiFormControl-marginDense': {
                marginTop: '6px',
                marginBottom: 0
              }
            }}
          />
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block', marginTop: '6px' }}
          >
            {t('calendar.import_url_description')}
          </Typography>
        </Box>
      )}

      {/* Form group 3: Import to */}
      <Box sx={{ mt: 2 }}>
        <FormControl
          fullWidth
          size="small"
          margin="dense"
          sx={{
            '&.MuiFormControl-root.MuiFormControl-marginDense': {
              marginTop: '6px',
              marginBottom: 0
            }
          }}
        >
          <CalendarSelector
            userId={userId}
            importTarget={importTarget}
            setImportTarget={setImportTarget}
          />
        </FormControl>
      </Box>

      {/* Form group 4: SettingsTab (when importing to new calendar) */}
      {importTarget === 'new' && (
        <Box sx={{ mt: 2 }}>
          <SettingsTab {...newCalParams} />
        </Box>
      )}
    </>
  )
}
